import React, { ComponentType, useCallback, useMemo, useState } from "react";

import type { Dayjs } from "dayjs";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import RebetLoaderJs from "../../../shared/RebetLoader/RebetLoader";
import GeographicUserInsightsJs from "../../../shared/Dashboard/GeographicUserInsights/GeographicUserInsights";
import Select from "../../../shared/Select/Select";
import {
  useGetFinancialsByState,
  type FinancialsStateRow,
} from "../../../../queries/financials/useGetFinancialsByState";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../../utils/Constants";
import { getAdminNow } from "../../../../utils/dateService";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../../utils/narrowViewportChartDates";
import { getStatesByCountry } from "../../../../utils/MapStateName";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";

import styles from "./FinancialsByState.module.css";

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;
const RebetLoader = RebetLoaderJs as ComponentType<any>;
const GeographicUserInsights = GeographicUserInsightsJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

const METRIC_OPTIONS = [
  { value: "withdraw", label: "Withdraw by State" },
  { value: "revenue", label: "Revenue by State" },
  { value: "deposit", label: "Deposit by State" },
] as const;

type MetricKey = (typeof METRIC_OPTIONS)[number]["value"];

/**
 * Resolve the abbr → name list for the active country from the
 * `@svg-maps`-backed records in `MapStateName`. Falls back to the US
 * list — matches the legacy V1 behaviour and avoids a blank map when
 * the TCM config slice hasn't loaded yet.
 */
function buildStateList(
  country: string | undefined,
): ReadonlyArray<{ name: string; abbr: string }> {
  // `getStatesByCountry` is a JS module; cast to the concrete record
  // shape it returns so TS treats `name` as a string.
  const states = getStatesByCountry(country ?? "USA") as Record<string, string>;
  return Object.entries(states).map(([abbr, name]) => ({ abbr, name }));
}

const ZERO_METRICS: Pick<FinancialsStateRow, "withdraw" | "revenue" | "deposit"> =
  {
    withdraw: 0,
    revenue: 0,
    deposit: 0,
  };

interface FinancialsByStateProps {
  country?: string;
}

const FinancialsByState: React.FC<FinancialsByStateProps> = ({
  country = "USA",
}) => {
  // Default range is month-to-date (1st of the current month → today). The
  // upper bound is `endOf("day")` rather than `endOf("month")` so the range
  // never extends past today, regardless of where in the month we are.
  // At 768px and below `defaultDateRangeForViewport` swaps in last 7 days.
  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.startOf("month"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.CURRENT_MONTH),
  );
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>("revenue");

  const currencySymbol = useCurrencySymbol() ?? "$";

  const dateFilters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    }),
    [dates],
  );

  const { data: stateData = [], isFetching, isLoading, isError } =
    useGetFinancialsByState(dateFilters);

  const rowsByAbbr = useMemo(() => {
    const map = new Map<string, FinancialsStateRow>();
    for (const row of stateData) {
      map.set(row.abbr.toUpperCase(), row);
    }
    return map;
  }, [stateData]);

  // Pick the abbr-set for the client's country (US for ReBet/DoggHouse,
  // MX for Infinito/Estadio Gana, NG for BetCorrect/Scorama).
  const stateList = useMemo(() => buildStateList(country), [country]);

  const mapData = useMemo(
    () =>
      stateList.map((s) => {
        const metrics = rowsByAbbr.get(s.abbr) ?? {
          name: s.name,
          abbr: s.abbr,
          ...ZERO_METRICS,
        };
        return {
          name: s.name,
          abbr: s.abbr,
          amount: metrics[selectedMetric],
        };
      }),
    [rowsByAbbr, selectedMetric, stateList],
  );

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> query key, which refetches.
  }, []);

  const selectedLabel = useMemo(
    () =>
      METRIC_OPTIONS.find((opt) => opt.value === selectedMetric)?.label ??
      "Financials by State",
    [selectedMetric],
  );

  const exportRows = useMemo(
    () =>
      stateList.map((s) => {
        const metrics = rowsByAbbr.get(s.abbr) ?? {
          name: s.name,
          abbr: s.abbr,
          ...ZERO_METRICS,
        };
        return {
          state: s.name,
          abbreviation: s.abbr,
          withdraw: metrics.withdraw,
          revenue: metrics.revenue,
          deposit: metrics.deposit,
        };
      }),
    [rowsByAbbr, stateList],
  );

  const showLoading = isLoading || isFetching;

  return (
    <section className={styles.card} aria-label={selectedLabel}>
      <header className={styles.header}>
        <Select
          className={styles.metricSelect}
          value={selectedMetric}
          options={METRIC_OPTIONS as any}
          onChange={(value) => setSelectedMetric(value as MetricKey)}
          aria-label="Metric"
        />
        <div className={styles.controls}>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="financials-by-state.xlsx"
          />
        </div>
      </header>

      {isError ? (
        <p
          className={styles.errorBanner}
          role="alert"
          data-testid="financials-by-state-error"
        >
          {AppStrings.FINANCIALS_BY_STATE_LOAD_ERROR}
        </p>
      ) : null}

      <div className={styles.mapContainer}>
        {showLoading ? (
          <div className={styles.loaderWrapper} data-testid="chart-loader">
            <RebetLoader width={100} height={100} />
          </div>
        ) : isError ? null : (
          <GeographicUserInsights
            mapId="financials-by-state"
            data={mapData}
            isDollars={true}
            country={country}
            currencySymbol={currencySymbol}
          />
        )}
      </div>
    </section>
  );
};

export default FinancialsByState;
